import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Helmet } from 'react-helmet';
import { Divider, Form, Skeleton, Typography } from 'antd';

import { PageProps } from '@/types/PageProps';
import { SaveButton } from '@/components/_common';

import {
  MailConfigurationFormValues,
  SenderType,
} from '@/core/domain/mail/MailConfigurationController/MailConfigurationController.types';
import {
  useMailConfiguration,
  useMailConfigurationCreate,
  useMailConfigurationUpdate,
  useMailConfigurationVerify,
} from '@/core/domain/mail/MailConfigurationController/MailConfigurationController.hooks';
import mailBase64ToUrl from '@/utils/functions/mailBase64ToUrl';
import { useWindowSize } from '@/hooks';
import AccessDenied from '@/components/_common/AccessDenied';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import EmailSettingsFooterActions from '@/components/Settings/Email/EmailSettingsFooterActions';
import EmailSettingsForm from '@/components/Settings/Email/EmailSettingsForm';

const { Title } = Typography;

function EmailSettingsPage({ route }: PageProps) {
  const intl = useIntl();
  const [form] = Form.useForm<MailConfigurationFormValues>();
  const [uploading, setUploading] = useState(false);
  const { data, isLoading } = useMailConfiguration({
    sender: SenderType.matrix,
  });
  const { width } = useWindowSize();

  const isExistingMail = !!data?.id;

  useEffect(() => {
    if (data) {
      form.setFieldsValue({ ...data });
    }
  }, [data]);

  const mutationCreateConfiguration = useMailConfigurationCreate();
  const mutationUpdateConfiguration = useMailConfigurationUpdate();
  const mailConfigurationVerify = useMailConfigurationVerify();

  const handleSubmit = async () => {
    const values = await form.validateFields();
    let newMessage = '';

    try {
      newMessage = await mailBase64ToUrl(values.signature, setUploading);

      if (data) {
        return mutationUpdateConfiguration.mutateAsync({
          id: data.id,
          ...values,
          sender: SenderType.matrix,
          signature: newMessage,
        });
      } else {
        return mutationCreateConfiguration.mutateAsync({
          ...values,
          sender: SenderType.matrix,
          signature: newMessage,
        });
      }
    } finally {
      form.setFieldValue('signature', newMessage);
    }
  };

  const handleVerifyEmail = async () => {
    const values = await form.validateFields();

    await mailConfigurationVerify.mutateAsync({
      id: Number(data?.id),
      mail: values?.mail,
      password: values?.password,
      port: values?.port,
      smtp: values?.smtp,
      ssl: values?.ssl,
      tls: values?.tls,
    });
  };

  if (!hasPermissions([Authorities.CONFIGURATION_USER_EDIT_SYSTEM_SETTINGS])) {
    return <AccessDenied />;
  }

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />
      <div className="page">
        <div className="page-header">
          <Title level={width < 576 ? 5 : 4}>
            {intl.formatMessage({ id: route.name })}
          </Title>

          {!(width < 992) && (
            <SaveButton
              onClick={handleSubmit}
              loading={
                mutationCreateConfiguration.isLoading ||
                mutationUpdateConfiguration.isLoading ||
                uploading
              }
            >
              {intl.formatMessage({
                id: 'commons.form.button.save',
              })}
            </SaveButton>
          )}
        </div>

        <Divider />

        <Skeleton loading={isLoading}>
          <EmailSettingsForm form={form} isExistingMail={isExistingMail} />

          <EmailSettingsFooterActions
            handleSubmit={handleSubmit}
            saveLoading={
              mutationCreateConfiguration.isLoading ||
              mutationUpdateConfiguration.isLoading ||
              uploading
            }
            handleVerifyEmail={handleVerifyEmail}
            verifyEmailLoading={mailConfigurationVerify.isLoading}
          />
        </Skeleton>
      </div>
    </>
  );
}

export default EmailSettingsPage;
